<template>
    <div>
        <div class="d-flex align-center flex-wrap px-2">
            <v-text-field
                v-model="search"
                dense
                outlined
                single-line
                hide-details
                clearable
                class="mr-2 campo-busca"
                label="Empresa, payment id, txid.."
                append-icon="mdi-magnify"
                @keypress.enter="aplicar"
                @click:clear="aplicar"
            />

            <v-spacer/>

            <PredefinicoesMenu
                :itens="predefinicoes"
                :ativa-id="ativaId"
                :sujo="sujo"
                :nome-em-uso="nomeEmUso"
                @aplicar="aplicarPredefinicao"
                @padrao="aplicarPadrao"
                @salvar="salvarPredefinicao"
                @salvar-como="criarPredefinicao"
                @renomear="renomearPredefinicao"
                @duplicar="duplicarPredefinicao"
                @remover="removerPredefinicao"
            />

            <ColunasMenu
                v-if="!agrupado"
                v-model="colunas"
                :catalogo="catalogoColunas"
            />

            <FiltrosMenu
                v-model="filtros"
                :catalogo="catalogoFiltros"
                @aplicar="aplicar"
            />

            <PeriodoFilter
                :key="periodoKey"
                v-model="periodo"
                defaultPeriodo="Últimos 30 dias"
                :label="rotuloPeriodo"
                @update="aplicar"
            />
        </div>

        <div v-if="chips.length" class="d-flex flex-wrap align-center px-4 pt-2">
            <v-chip
                v-for="chip in chips"
                :key="chip.chave"
                class="mr-2 mb-1"
                small
                close
                @click:close="removerFiltro(chip.chave)"
            >
                <span class="font-weight-medium mr-1">{{ chip.titulo }}:</span>
                {{ chip.texto }}
            </v-chip>

            <v-btn text x-small class="mb-1" @click="limparFiltros">Limpar tudo</v-btn>
        </div>

        <v-card elevation="2" class="ma-1">
            <v-card-text class="pa-2">
                <v-data-table
                    fixed-header
                    :headers="headers"
                    :items="dados"
                    :mobileBreakpoint="0"
                    :loading="loading"
                    :height="alturaTabela"
                    :disable-pagination="true"
                    :hide-default-footer="true"
                    :options.sync="options"
                    :custom-sort="manterOrdemDoServidor"
                    dense
                >
                    <template v-slot:no-data>
                        <div class="py-8 text-center grey--text">
                            <v-icon large color="grey">mdi-filter-remove-outline</v-icon>
                            <div class="mt-2">Nenhuma taxa para os filtros atuais.</div>
                            <v-btn
                                v-if="chips.length"
                                text
                                small
                                color="primary"
                                class="mt-1"
                                @click="limparFiltros"
                            >Limpar filtros</v-btn>
                        </div>
                    </template>

                    <!-- modo transação -->
                    <template v-slot:item.paid_at="{ item }">
                        {{ formatDate(item.paid_at) }}
                    </template>

                    <template v-slot:item.created_at="{ item }">
                        {{ formatDate(item.created_at) }}
                    </template>

                    <template v-slot:item.empresa="{ item }">
                        {{ item.empresa_id }} - {{ item.empresa_fantasia }}
                    </template>

                    <template v-slot:item.empresa_ativa="{ item }">
                        <v-icon small :color="item.empresa_ativa ? 'success' : 'error'">
                            mdi-circle-medium
                        </v-icon>
                    </template>

                    <template v-slot:item.metodo="{ item }">
                        {{ rotuloDe(metodos, item.metodo) }}
                    </template>

                    <template v-slot:item.conta="{ item }">
                        {{ rotuloDe(contas, item.conta) }}
                    </template>

                    <template v-slot:item.valor_total="{ item }">
                        R$ {{ formatPrice(item.valor_total / 100) }}
                    </template>

                    <template v-slot:item.valor_taxa="{ item }">
                        <span class="green--text">R$ {{ formatPrice(item.valor_taxa / 100) }}</span>
                    </template>

                    <template v-slot:item.percentual="{ item }">
                        {{ item.percentual === null ? '—' : `${item.percentual}%` }}
                    </template>

                    <template v-slot:item.taxa_configurada="{ item }">
                        <v-tooltip bottom :disabled="!divergente(item)">
                            <template v-slot:activator="{ on, attrs }">
                                <span
                                    v-bind="attrs"
                                    v-on="on"
                                    :class="divergente(item) ? 'orange--text' : ''"
                                >{{ textoTaxaConfigurada(item) }}</span>
                            </template>
                            <span>Cobrado {{ item.percentual }}%, diferente do configurado</span>
                        </v-tooltip>
                    </template>

                    <template v-slot:item.mp_payment_id="{ item }">
                        <span class="link" @click="copiar(item.mp_payment_id)">
                            {{ item.mp_payment_id }}
                        </span>
                    </template>

                    <template v-slot:item.external_reference="{ item }">
                        <span class="link" @click="copiar(item.external_reference)">
                            {{ (item.external_reference || '').slice(0, 8) }}…
                        </span>
                    </template>

                    <!-- modo agrupado -->
                    <template v-slot:item.rotulo="{ item }">
                        {{ agrupadoPorConta ? rotuloDe(contas, item.rotulo) : item.rotulo }}
                    </template>

                    <template v-slot:item.total_processado="{ item }">
                        R$ {{ formatPrice(item.total_processado / 100) }}
                    </template>

                    <template v-slot:item.total_taxa="{ item }">
                        <span class="green--text">R$ {{ formatPrice(item.total_taxa / 100) }}</span>
                    </template>

                    <template v-slot:item.percentual_efetivo="{ item }">
                        {{ item.percentual_efetivo === null ? '—' : `${item.percentual_efetivo}%` }}
                    </template>
                </v-data-table>

                <Totalizadores
                    v-if="podeVerTotais"
                    class="mt-2"
                    :itens="itensTotais"
                    :loading="loadingTotais"
                />

                <Paginate v-model="pagination" @input="consultar"/>
            </v-card-text>
        </v-card>
    </div>
</template>

<script>
import ColunasMenu from '@/components/ColunasMenu';
import FiltrosMenu from '@/components/FiltrosMenu';
import Paginate from '@/components/Paginate';
import PeriodoFilter from '@/components/PeriodoFilter';
import PredefinicoesMenu from '@/components/PredefinicoesMenu';
import Totalizadores from '@/components/Totalizadores';
import consultaMixin from './consultaMixin';
import { COLUNAS_TAXAS_AGRUPADAS, catalogoColunasTaxas } from './colunas';
import { catalogoFiltrosTaxas, definirEmpresasDoFiltroTaxas } from './filtros';
import { CAMPOS_DATA_TAXA, CONTAS_MP, METODOS_MP, rotuloDe } from './status';

export default {
    name: 'TabTaxasMp',

    components: {
        ColunasMenu,
        FiltrosMenu,
        Paginate,
        PeriodoFilter,
        PredefinicoesMenu,
        Totalizadores,
    },

    mixins: [consultaMixin],

    data: () => ({
        endpoint: 'taxas-mercado-pago',
        chavePredefinicoes: 'zaper-control-taxas-mp-predefinicoes',
        permissaoConsultar: 'zapercontrol_cobrancas_taxas_mp_consultar',
        permissaoTotais: 'zapercontrol_cobrancas_taxas_mp_totalizadores',
        catalogoColunas: catalogoColunasTaxas,
        catalogoFiltros: catalogoFiltrosTaxas,
        colunas: catalogoColunasTaxas.colunasPadrao(),
        filtros: catalogoFiltrosTaxas.filtrosPadrao(),

        metodos: METODOS_MP,
        contas: CONTAS_MP,
        ultimoAgrupado: false,
    }),

    computed: {
        agrupado() {
            return !!this.filtros.agrupar_por;
        },

        agrupadoPorConta() {
            return this.filtros.agrupar_por === 'conta';
        },

        headers() {
            if (this.agrupado) {
                return COLUNAS_TAXAS_AGRUPADAS;
            }

            return this.colunas
                .map(valor => this.catalogoColunas.coluna(valor))
                .filter(Boolean)
                .map(c => ({ text: c.text, value: c.value, sortable: !!c.sortable }));
        },

        alturaTabela() {
            const extras = (this.chips.length ? 50 : 0) + (this.podeVerTotais ? 76 : 0);
            return this.$vuetify.breakpoint.height - 300 - extras;
        },

        rotuloPeriodo() {
            return rotuloDe(CAMPOS_DATA_TAXA, this.filtros.campo_data) || 'Período';
        },

        itensTotais() {
            const t = this.totais || {};
            const real = centavos => `R$ ${this.formatPrice((centavos || 0) / 100)}`;

            const itens = [
                { label: 'Transações', valor: `${t.qtd_transacoes || 0}`, detalhe: `${t.qtd_empresas || 0} empresas` },
                { label: 'Processado', valor: real(t.total_processado) },
                { label: 'Taxa arrecadada', valor: real(t.total_taxa), cor: 'green', destaque: true },
                { label: 'Taxa efetiva', valor: `${t.taxa_efetiva_percentual || 0}%` },
                { label: 'Ticket médio', valor: real(t.ticket_medio_transacao), detalhe: 'da transação' },
                { label: 'Taxa média', valor: real(t.ticket_medio_taxa), detalhe: 'por transação' },
            ];

            (t.por_metodo || []).forEach(linha => {
                itens.push({
                    label: rotuloDe(METODOS_MP, linha.metodo),
                    valor: real(linha.total_taxa),
                    detalhe: `${linha.qtd_transacoes} transações`,
                });
            });

            const porConta = t.por_conta || [];

            if (porConta.length > 1) {
                porConta.forEach(linha => {
                    itens.push({
                        label: `Conta ${rotuloDe(CONTAS_MP, linha.conta)}`,
                        valor: real(linha.total_taxa),
                        detalhe: `${linha.qtd_transacoes} transações`,
                    });
                });
            }

            return itens;
        },
    },

    created() {
        this.carregarEmpresas();
    },

    methods: {
        rotuloDe,

        aplicar() {
            if (this.agrupado !== this.ultimoAgrupado) {
                this.ultimoAgrupado = this.agrupado;
                this.options = { sortBy: [], sortDesc: [] };
                this.ultimaOrdenacao = JSON.stringify([[], []]);
            }

            this.pagination.current_page = 1;
            this.consultar();
        },

        carregarEmpresas() {
            if (!this.podeConsultar) {
                return;
            }

            this.$http.get('taxas-mercado-pago/empresas')
                .then(resp => {
                    definirEmpresasDoFiltroTaxas(resp.data.data || []);
                    this.filtros = { ...this.filtros };
                })
                .catch(() => {});
        },

        divergente(item) {
            if (!item.taxa_configurada || item.percentual === null) {
                return false;
            }

            return Math.abs(Number(item.percentual) - Number(item.taxa_configurada.percentual)) > 0.05;
        },

        textoTaxaConfigurada(item) {
            const cfg = item.taxa_configurada;

            if (!cfg) {
                return '—';
            }

            return cfg.fixo
                ? `${cfg.percentual}% + R$ ${this.formatPrice(cfg.fixo)}`
                : `${cfg.percentual}%`;
        },

        copiar(valor) {
            navigator.clipboard.writeText(String(valor || ''));
            this.notify('Copiado');
        },
    },
};
</script>

<style scoped>
.campo-busca {
    max-width: 260px;
}

.link {
    cursor: pointer;
    text-decoration: underline;
}
</style>
